import { htmlEscape } from "./html-escape.mjs";
import { formatFieldValue, substituteFieldTemplate } from "./arcgis-popup-fields.mjs";

// The raw property dump: every non-null, non-"Null", non-__gs-namespaced property as an escaped
// row, keyed by its service field name. This is the fallback rendering used whenever no field
// presentation (arcgis-popup-fields.mjs) is available, or the presentation matches nothing.
function rawRows(properties) {
	let rows = "";
	if (properties != null) {
		for (const name in properties) {
			if (!Object.prototype.hasOwnProperty.call(properties, name)) {
				continue;
			}
			// "__gs" is this app's reserved namespace for properties it stamps onto features
			// (currently the Arcade shim's synthetic classifying field, SYNTHETIC_FIELD in
			// arcade-renderer.mjs). They're internal, never service data, so never shown. Other
			// underscore-prefixed names stay visible — query-layer services can expose them.
			if (name.startsWith("__gs")) {
				continue;
			}
			const value = properties[name];
			if (value === null || value === undefined || value === "") {
				continue;
			}
			// ArcGIS represents empty attributes as the literal string "Null" (the identify
			// operation behind image-mode popups always stringifies values this way); showing
			// those rows is noise, same as a true null.
			if (typeof value === "string" && value.trim().toLowerCase() === "null") {
				continue;
			}
			rows += `<tr><td>${htmlEscape(name)}</td><td>${htmlEscape(value)}</td></tr>`;
		}
	}
	return rows;
}

// Rows driven by a field presentation (arcgis-popup-fields.mjs): authored order, labels, and
// value formatting. Properties are matched case-insensitively (item fieldInfos occasionally
// disagree with the service's field-name casing). The same null/"Null" noise rules as the raw
// dump apply.
function presentationRows(properties, presentation) {
	if (properties == null) {
		return "";
	}
	const byLower = new Map();
	for (const name in properties) {
		if (Object.prototype.hasOwnProperty.call(properties, name)) {
			byLower.set(name.toLowerCase(), properties[name]);
		}
	}
	let rows = "";
	for (const field of presentation.fields) {
		const value = byLower.get(field.name.toLowerCase());
		if (value === null || value === undefined || value === "") {
			continue;
		}
		if (typeof value === "string" && value.trim().toLowerCase() === "null") {
			continue;
		}
		rows += `<tr><td>${htmlEscape(field.label)}</td><td>${htmlEscape(formatFieldValue(value, field))}</td></tr>`;
	}
	return rows;
}

// A presentation applies to a feature when at least one presented field name exists among the
// property keys. Zero overlap means the presentation is wrong for this data shape (service/item
// drift, an alias remap that didn't take) and the raw dump is the honest fallback. Overlap with
// nothing-but-null values is different: the presentation applies, the feature is just empty, and
// falling back would resurrect fields the author explicitly hid.
function presentationApplies(properties, presentation) {
	if (properties == null) {
		return false;
	}
	const keys = new Set(Object.keys(properties).map((key) => key.toLowerCase()));
	return presentation.fields.some((field) => keys.has(field.name.toLowerCase()));
}

export function buildFeaturePropertiesTable(properties, labels, compact, presentation) {
	const hasPresentation = presentation != null && Array.isArray(presentation.fields) && presentation.fields.length > 0;
	const rows = hasPresentation && presentationApplies(properties, presentation)
		? presentationRows(properties, presentation)
		: rawRows(properties);
	const compactClass = compact === true ? " feature-properties-table-compact" : "";
	return `<table class="feature-properties-table table table-striped${compactClass}" style="width:100%">` +
		`<thead><tr><th>${htmlEscape(labels.property)}</th><th>${htmlEscape(labels.value)}</th></tr></thead>` +
		`<tbody>${rows}</tbody></table>`;
}

// Builds the ArcGIS feature popup shared by the track map (map-base.js) and the MapLayer admin
// preview (map.mjs): a table of the feature's non-null properties that the map's "popupopen"
// handler upgrades to a searchable, sortable DataTable. Every name/value passes through htmlEscape,
// so external attribute values can't inject markup. `labels` supplies the localized header text as
// { property, value } because each bundle sources its strings differently (the tracking bundle's
// seeded `strings` object vs. window.GSatArcgisPreview.labels set by the admin page). The table-only
// variant buildFeaturePropertiesTable serves the merged identify popup with multiple property tables.
// The optional `presentation` (arcgis-popup-fields.mjs's buildFieldPresentation output) renders a
// substituted title above the table and labeled/ordered/formatted rows in place of the raw dump;
// omitted, or matching nothing, it falls back to today's raw property table with no title.
export function buildFeaturePropertiesPopup(properties, labels, presentation) {
	const title = presentation != null ? substituteFieldTemplate(presentation.title, properties, presentation.fields) : null;
	const heading = title != null
		? `<div class="feature-properties-title font-weight-bold mb-1">${htmlEscape(title)}</div>`
		: "";
	return "<div class=\"feature-properties-popup standard-ui\">" + heading + buildFeaturePropertiesTable(properties, labels, false, presentation) + "</div>";
}

// jQuery is resolved from the page globals at call time because the two consuming pages alias it
// differently (the track page defines $j; the admin preview page has the standard $/jQuery).
function jq() {
	return window.$j || window.jQuery;
}

// Turns the feature-properties tables inside a just-opened popup into DataTables (search + sort, no
// paging, vertically scrollable via the popup's own maxHeight so long property lists don't grow the
// popup unbounded). Only acts on ArcGIS feature popups; other popups have no such tables. Wire as the
// map's "popupopen" handler.
export function initFeaturePropertiesDataTable(e) {
	const popup = e.popup;
	// Defer to the next tick so the popup table is in the DOM before DataTables initialises.
	setTimeout(() => {
		const $j = jq();
		const popupEl = popup.getElement == null ? null : popup.getElement();
		if (popupEl == null) {
			return;
		}
		const $tables = $j(popupEl).find("table.feature-properties-table");
		$tables.each(function () {
			if ($j.fn.dataTable && $j.fn.dataTable.isDataTable(this)) {
				return;
			}
			// Merged identify popups mark their tables compact: several tables in one popup
			// each with a search box is noise, so compact tables keep sorting only.
			const compact = this.classList.contains("feature-properties-table-compact");
			// Place the search box on the left via DataTables' own layout config: a flex wrapper
			// makes the content-width filter a flex item at flex-start, so its default
			// text-align:right has nothing to push against. Avoids overriding DataTables' CSS.
			$j(this).dataTable({
				dom: compact ? "t" : "<'d-flex'f>t",
				paging: false,
				info: false,
				lengthChange: false,
				searching: !compact,
				ordering: true,
				autoWidth: false,
				order: [],
			});
		});
		// Do NOT call popup.update() after this: it re-applies the popup's stored content,
		// which rebuilds a plain table and throws away the DataTables wrapper. The popup
		// already sizes/scrolls itself via its maxWidth/maxHeight options.
	}, 0);
}

// Cleans up the DataTables when the popup closes (each open rebuilds the tables). Wire as the map's
// "popupclose" handler.
export function destroyFeaturePropertiesDataTable(e) {
	const popupEl = e.popup.getElement();
	if (popupEl == null) {
		return;
	}
	const $j = jq();
	$j(popupEl).find("table.feature-properties-table").each(function () {
		if ($j.fn.dataTable && $j.fn.dataTable.isDataTable(this)) {
			$j(this).DataTable().destroy();
		}
	});
}
